import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { subscriptionsApi, paymentsApi } from '../lib/api';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { promoPriceFor } from '../hooks/usePublicPromo';
import {
  CheckCircle,
  Crown,
  Zap,
  AlertTriangle,
  Sparkles,
  Calendar,
  Tag,
  Flame,
  ShieldCheck,
} from 'lucide-react';

// Simple countdown ticker for the promo end banner.
function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const t = Date.parse(targetIso);
  if (Number.isNaN(t)) return null;
  const diff = Math.max(0, t - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    expired: diff <= 0,
  };
}

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [publicPromo, setPublicPromo] = useState(null);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, source: 'auto'|'manual' }

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        subscriptionsApi.getPlans(),
        subscriptionsApi.getCurrent(),
      ]);
      setPlans(plansRes.data.plans);
      setCurrentSub(subRes.data);

      // Pick first public, non-expired promo (server already filters, but
      // we double-check to survive cache/staleness).
      const promos = plansRes.data.promos || [];
      const now = Date.now();
      const active = promos.find((p) => {
        if (!p.expires_at) return true;
        const exp = Date.parse(p.expires_at);
        return Number.isNaN(exp) || exp > now;
      }) || null;
      setPublicPromo(active);

      // Auto-apply: URL param wins over the active public promo. This lets
      // legacy/hidden codes (FLY50, staff codes) survive a deep-link and be
      // validated at checkout even if they don't match the currently
      // promoted public code (SUMMER).
      const urlCode = (searchParams.get('promo') || '').toUpperCase().trim();
      if (urlCode) {
        setPromoCode(urlCode);
        setAppliedPromo({ code: urlCode, source: 'auto' });
      } else if (active) {
        setPromoCode(active.code);
        setAppliedPromo({ code: active.code, source: 'auto' });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Promo details currently in effect (auto or manual) — resolved by matching
  // against the public promo. Legacy codes (FLY50) still get sent to the
  // backend during checkout even if not in `publicPromo`.
  const effectivePromo = useMemo(() => {
    if (!appliedPromo) return null;
    if (publicPromo && appliedPromo.code === publicPromo.code) return publicPromo;
    return null;
  }, [appliedPromo, publicPromo]);

  const countdown = useCountdown(effectivePromo?.expires_at);

  const handleUpgrade = async (planType) => {
    setCheckoutLoading(planType);
    trackEvent('checkout_started', {
      plan: planType,
      has_promo: !!appliedPromo,
      promo_code: appliedPromo?.code || null,
    });
    try {
      const originUrl = window.location.origin;
      // Send the promo iff it applies to this plan (per public metadata).
      let code = null;
      if (appliedPromo && effectivePromo?.applicable_plans?.includes(planType)) {
        code = appliedPromo.code;
      } else if (appliedPromo && !effectivePromo) {
        // Legacy / manual code not in publicPromo: still forward it and let
        // the backend accept or reject.
        code = appliedPromo.code;
      }
      const response = await paymentsApi.createCheckout(planType, originUrl, code);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleApplyPromo = () => {
    const code = promoCode.toUpperCase().trim();
    if (!code) return;
    // Match against public promo first for a nice UX message.
    if (publicPromo && code === publicPromo.code) {
      setAppliedPromo({ code, source: 'manual' });
      toast.success(`🎉 Code ${code} appliqué !`);
      // Reflect in URL so it survives a share/refresh.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('promo', code);
        return next;
      }, { replace: true });
      return;
    }
    // Legacy fallback: accept known-format codes and let backend validate at
    // checkout time. This keeps FLY50 workable for anyone who still holds it.
    setAppliedPromo({ code, source: 'manual' });
    toast.success(`Code ${code} enregistré — validation au paiement`);
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('promo');
      return next;
    }, { replace: true });
  };

  if (loading) {
    return (
      <DashboardLayout title="Abonnement">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  const isSolo = user?.subscription_status === 'solo';

  return (
    <DashboardLayout title="Abonnement">
      {/* Public promo banner — SUMMER first, but future public promos will
          flow through the same UI automatically thanks to the shared shape. */}
      {!isSolo && effectivePromo && (
        <div
          className="mb-8 p-4 md:p-5 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 rounded-xl text-white shadow-lg"
          data-testid="summer-promo-dashboard-banner"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Flame className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-lg leading-tight">🔥 Offre d&apos;été — jusqu&apos;à -53%</p>
                <p className="text-white/90 text-sm">
                  Solo <span className="font-bold">8,90€</span>/mois · Annuel{' '}
                  <span className="font-bold">89€</span>/an · Code{' '}
                  <span className="font-mono bg-white/20 px-1.5 py-0.5 rounded font-bold">
                    {effectivePromo.code}
                  </span>{' '}
                  {appliedPromo?.code === effectivePromo.code && (
                    <span className="inline-flex items-center gap-1 ml-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                      <CheckCircle className="w-3 h-3" /> appliqué
                    </span>
                  )}
                </p>
              </div>
            </div>
            {countdown && !countdown.expired && (
              <div className="flex items-center gap-3" data-testid="summer-promo-countdown-dashboard">
                <div className="text-right hidden sm:block">
                  <div className="text-xs uppercase tracking-wider opacity-80">
                    {effectivePromo.recurring_weekly ? 'Fin de la semaine' : 'Fin dans'}
                  </div>
                  {effectivePromo.recurring_weekly && (
                    <div className="text-[10px] opacity-70">Se renouvelle chaque lundi</div>
                  )}
                </div>
                <div className="flex items-center gap-2 font-mono tabular-nums text-lg font-bold">
                  <span>{String(countdown.days).padStart(2, '0')}j</span>
                  <span>{String(countdown.hours).padStart(2, '0')}h</span>
                  <span>{String(countdown.minutes).padStart(2, '0')}m</span>
                  <span className="opacity-80">{String(countdown.seconds).padStart(2, '0')}s</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current Status */}
      <div className={`
        p-6 rounded-lg border-2 mb-8
        ${isSolo ? 'bg-sky-50 border-sky-300' : 'bg-slate-50 border-slate-200'}
      `}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {isSolo ? (
                <Crown className="w-6 h-6 text-sky-600" />
              ) : (
                <Zap className="w-6 h-6 text-slate-500" />
              )}
              <h2 className="text-xl font-bold text-slate-900 font-[Barlow]">
                Plan {isSolo ? 'Solo' : 'Gratuit'}
              </h2>
            </div>
            <p className="text-slate-600">
              {isSolo
                ? 'Vous bénéficiez de toutes les fonctionnalités premium.'
                : `Il vous reste ${Math.max(0, 3 - (currentSub?.missions_count || 0))} mission(s) gratuite(s).`}
            </p>
          </div>
          {isSolo && currentSub?.subscription && (
            <div className="text-right">
              <p className="text-sm text-slate-500">Prochain renouvellement</p>
              <p className="font-semibold text-slate-900">
                {new Date(currentSub.subscription.renewal_date).toLocaleDateString('fr-FR')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Plans Grid */}
      <h3 className="text-lg font-semibold text-slate-900 mb-6 font-[Barlow]">
        {isSolo ? 'Votre plan actuel' : 'Choisissez votre plan'}
      </h3>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl">
        {plans.map((plan) => {
          const isCurrentPlan =
            (plan.id === 'free' && !isSolo) ||
            ((plan.id === 'solo' || plan.id === 'solo_annual') && isSolo);

          const isAnnual = plan.id === 'solo_annual';
          const isRecommended = plan.id === 'solo_annual';

          // Compute promo price for this plan (shared with LandingPage).
          const { finalPrice, hasPromo } = promoPriceFor(
            effectivePromo,
            plan.id,
            plan.price,
          );
          const displayPrice = hasPromo ? finalPrice : plan.price;
          const monthlyEq = isAnnual
            ? (hasPromo ? (finalPrice / 12).toFixed(2) : plan.price_monthly_equivalent?.toFixed(2))
            : null;
          const savings = isAnnual
            ? (hasPromo ? Math.round(19 * 12 - finalPrice) : 38)
            : null;

          return (
            <div
              key={plan.id}
              className={`
                relative bg-white border-2 rounded-xl p-6 transition-all duration-200
                ${isRecommended ? 'border-emerald-400 shadow-lg shadow-emerald-100 scale-105' :
                  plan.id === 'solo' ? 'border-sky-300' : 'border-slate-200'}
                ${isCurrentPlan ? 'ring-2 ring-sky-500 ring-offset-2' : ''}
                hover:shadow-lg
              `}
              data-testid={`plan-card-${plan.id}`}
            >
              {/* Badge */}
              {isAnnual && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-full uppercase flex items-center gap-1 shadow-md">
                  <Sparkles className="w-3 h-3" />
                  {hasPromo ? '-53% Promo été' : '2 mois offerts'}
                </div>
              )}
              {plan.id === 'solo' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-sky-500 text-white text-xs font-semibold rounded-full uppercase">
                  {hasPromo ? '🔥 Promo été' : 'Populaire'}
                </div>
              )}

              {isCurrentPlan && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Actuel
                  </span>
                </div>
              )}

              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4
                ${isAnnual ? 'bg-gradient-to-br from-emerald-500 to-teal-500' :
                  plan.id === 'solo' ? 'bg-sky-500' : 'bg-slate-200'}`}>
                {isAnnual ? <Calendar className="w-6 h-6 text-white" /> :
                  plan.id === 'solo' ? <Crown className="w-6 h-6 text-white" /> :
                  <Zap className="w-6 h-6 text-slate-500" />}
              </div>

              <h3 className="text-xl font-bold text-slate-900 font-[Barlow]">{plan.name}</h3>

              <div className="mt-4 mb-2">
                {hasPromo && (
                  <span
                    className="text-2xl font-bold text-slate-400 line-through mr-2"
                    data-testid={`plan-${plan.id}-original-price`}
                  >
                    {plan.price}€
                  </span>
                )}
                <span
                  className={`text-4xl font-bold ${hasPromo ? 'text-orange-600' : 'text-slate-900'}`}
                  data-testid={`plan-${plan.id}-final-price`}
                >
                  {typeof displayPrice === 'number'
                    ? displayPrice.toString().replace('.', ',')
                    : displayPrice}
                  €
                </span>
                <span className="text-slate-500">
                  {plan.interval === 'year' ? '/an' : plan.interval === 'month' ? '/mois' : ''}
                </span>
              </div>

              {isAnnual && monthlyEq && (
                <div className="mb-4">
                  <span className={`text-sm font-medium ${hasPromo ? 'text-orange-700' : 'text-emerald-600'}`}>
                    soit {monthlyEq.replace('.', ',')}€/mois
                  </span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium ${
                    hasPromo ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {hasPromo ? '-53%' : `-${plan.discount_percent}%`}
                  </span>
                </div>
              )}

              {isAnnual && savings != null && (
                <div className={`mb-4 p-2 rounded-lg border ${
                  hasPromo ? 'bg-orange-50 border-orange-200' : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <p className={`text-sm font-medium text-center ${
                    hasPromo ? 'text-orange-700' : 'text-emerald-700'
                  }`}>
                    💰 Économisez {savings}€ {hasPromo ? 'vs. mensuel' : 'par an'}
                  </p>
                </div>
              )}

              {hasPromo && !isAnnual && (
                <div className="mb-3 p-2 rounded-lg border bg-orange-50 border-orange-200">
                  <p className="text-xs text-orange-700 text-center">
                    Prix promo — tarif standard {plan.price}€/mois
                  </p>
                </div>
              )}

              {/* Reassurance: none of our Solo plans (monthly nor annual) are
                  billed on a recurring Stripe Subscription. Each purchase is a
                  one-shot payment. Users have expressed anxiety about "trap"
                  subscriptions — this note is the single most important
                  conversion-lifting message on the pricing page. */}
              {(plan.id === 'solo' || plan.id === 'solo_annual') && (
                <div
                  className="mb-4 p-2.5 rounded-lg border bg-emerald-50 border-emerald-200 flex items-start gap-2"
                  data-testid={`plan-${plan.id}-no-autorenew`}
                >
                  <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-emerald-800 leading-snug">
                    <span className="font-semibold">Sans engagement · Aucun prélèvement automatique.</span>
                    <br />
                    Vous recevez un rappel par email avant l'échéance et choisissez librement de renouveler.
                  </div>
                </div>
              )}

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm text-slate-700">
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 ${
                      isAnnual ? 'text-emerald-500' : 'text-sky-500'
                    }`} />
                    {feature}
                  </li>
                ))}
                {plan.limitations?.map((limitation, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm text-slate-400">
                    <AlertTriangle className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    {limitation}
                  </li>
                ))}
              </ul>

              {plan.id === 'free' ? (
                <Button variant="outline" className="w-full" disabled>
                  {isCurrentPlan ? 'Plan actuel' : 'Plan de base'}
                </Button>
              ) : (
                <Button
                  className={`w-full ${
                    isAnnual
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600'
                      : isCurrentPlan
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-sky-500 hover:bg-sky-600'
                  } text-white font-semibold`}
                  disabled={isCurrentPlan || checkoutLoading !== null}
                  onClick={() => handleUpgrade(plan.id)}
                  data-testid={`upgrade-to-${plan.id}-btn`}
                >
                  {isCurrentPlan
                    ? 'Votre plan actuel'
                    : checkoutLoading === plan.id
                      ? 'Chargement...'
                      : isAnnual
                        ? '🚀 Choisir Annuel'
                        : 'Choisir Mensuel'}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Manual promo code input — always available so legacy codes (FLY50)
          keep working alongside the auto-applied public promo. */}
      {!isSolo && (
        <div className="mt-8 max-w-md" data-testid="manual-promo-block">
          <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
            <Tag className="w-4 h-4 text-slate-500" /> Vous avez un code promo ?
          </label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Ex : SUMMER"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              className="text-sm h-10 font-mono"
              data-testid="promo-code-input"
            />
            <Button variant="outline" onClick={handleApplyPromo} data-testid="apply-promo-btn">
              Appliquer
            </Button>
            {appliedPromo && (
              <Button variant="ghost" onClick={clearPromo} data-testid="clear-promo-btn">
                Retirer
              </Button>
            )}
          </div>
          {appliedPromo && (
            <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Code <span className="font-mono">{appliedPromo.code}</span> {appliedPromo.source === 'auto' ? 'appliqué automatiquement' : 'appliqué'}
            </p>
          )}
        </div>
      )}

      {/* FAQ */}
      <div className="mt-12 max-w-2xl">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 font-[Barlow]">Questions fréquentes</h3>
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="font-medium text-slate-900 mb-2">Puis-je annuler à tout moment ?</p>
            <p className="text-sm text-slate-600">
              Oui, vous pouvez annuler votre abonnement à tout moment. Vous conserverez l&apos;accès jusqu&apos;à la fin de votre période de facturation.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="font-medium text-slate-900 mb-2">Comment utiliser mon code promo ?</p>
            <p className="text-sm text-slate-600">
              Saisissez votre code dans le champ &laquo;&nbsp;Vous avez un code promo&nbsp;?&nbsp;&raquo; puis cliquez sur &laquo;&nbsp;Appliquer&nbsp;&raquo;. Le code est validé au moment du paiement.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="font-medium text-slate-900 mb-2">Quelle est la différence entre mensuel et annuel ?</p>
            <p className="text-sm text-slate-600">
              Le plan annuel offre 2 mois gratuits, soit une économie de 38€ par an. Les fonctionnalités sont identiques.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="font-medium text-slate-900 mb-2">Mes données sont-elles conservées si je repasse au plan gratuit ?</p>
            <p className="text-sm text-slate-600">
              Oui, toutes vos missions et données restent accessibles. Seule la limite de création de nouvelles missions s&apos;appliquera.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
